/**
 * DomHelpers - Utilities for DOM manipulation
 * All created elements use 'atmosphere-' prefix
 */

export interface CanvasOptions {
  width: number;
  height: number;
  style?: Partial<CSSStyleDeclaration>;
}

/**
 * Create a canvas element with atmosphere prefix
 */
export function createCanvas(id: string, options: CanvasOptions): HTMLCanvasElement {
  // Ensure ID has atmosphere prefix
  const prefixedId = id.startsWith('atmosphere-') ? id : `atmosphere-${id}`;
  
  // Remove existing canvas if present
  const existing = document.getElementById(prefixedId);
  if (existing) {
    existing.remove();
  }

  const canvas = document.createElement('canvas');
  canvas.id = prefixedId;
  canvas.className = 'atmosphere-canvas';
  canvas.width = options.width;
  canvas.height = options.height;

  // Apply CSS reset to prevent host page interference
  canvas.style.boxSizing = 'content-box';
  canvas.style.padding = '0';
  canvas.style.margin = '0';
  canvas.style.border = 'none';
  // Use !important via cssText for maximum specificity
  canvas.style.cssText += 'box-sizing: content-box !important; padding: 0 !important; margin: 0 !important; border: none !important;';

  // Apply additional styles
  if (options.style) {
    Object.assign(canvas.style, options.style);
  }

  return canvas;
}

/**
 * Remove an element from DOM
 */
export function removeElement(element: HTMLElement): void {
  if (element && element.parentNode) {
    element.parentNode.removeChild(element);
  }
}

/**
 * Create a style element with atmosphere prefix
 */
export function injectStyles(cssText: string): HTMLStyleElement {
  const styleId = 'atmosphere-injected-styles';
  
  // Remove existing styles
  const existing = document.getElementById(styleId);
  if (existing) {
    existing.remove();
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = cssText;
  document.head.appendChild(style);

  return style;
}

/**
 * Get or create a container element
 */
export function getOrCreateContainer(id: string = 'atmosphere-container'): HTMLElement {
  const prefixedId = id.startsWith('atmosphere-') ? id : `atmosphere-${id}`;
  
  let container = document.getElementById(prefixedId);
  
  if (!container) {
    container = document.createElement('div');
    container.id = prefixedId;
    container.className = 'atmosphere-container';
    document.body.appendChild(container);
  }

  return container;
}

